import { useState, useEffect } from 'react';
import { toast } from 'sonner';
import salesApiClient from '@/api/salesClient';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogOverlay } from '@/components/ui/dialog';
import { Plus, Users, Mail, Shield, Edit2, Trash2, UserCheck, MapPin, Copy, Send, Smartphone, QrCode, Link2, ExternalLink } from 'lucide-react';

export default function TeamManagement() {
  const [teamMembers, setTeamMembers] = useState([]);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isLocationDialogOpen, setIsLocationDialogOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState(null);
  const [locationLink, setLocationLink] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'sales_rep',
  });

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  const fetchTeamMembers = async () => {
    try {
      const response = await salesApiClient.get('/team/members');
      setTeamMembers(response.data);
    } catch (error) {
      toast.error('Failed to fetch team members');
    }
  };

  const handleAddMember = async (e) => {
    e.preventDefault();
    try {
      await salesApiClient.post('/team/members', formData);
      toast.success('Team member added successfully!');
      setIsAddDialogOpen(false);
      setFormData({ name: '', email: '', password: '', role: 'sales_rep' });
      fetchTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add team member');
    }
  };

  const handleEditMember = async (e) => {
    e.preventDefault();
    try {
      const updateData = { ...formData };
      if (!updateData.password) {
        delete updateData.password;
      }
      await salesApiClient.patch(`/team/members/${selectedMember.id}`, updateData);
      toast.success('Team member updated successfully!');
      setIsEditDialogOpen(false);
      setSelectedMember(null);
      setFormData({ name: '', email: '', password: '', role: 'sales_rep' });
      fetchTeamMembers();
    } catch (error) {
      toast.error('Failed to update team member');
    }
  };

  const handleDeleteMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName} from the team?`)) return;
    
    try {
      await salesApiClient.delete(`/team/members/${memberId}`);
      toast.success('Team member removed successfully!');
      fetchTeamMembers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove team member');
    }
  };

  const openEditDialog = (member) => {
    setSelectedMember(member);
    setFormData({
      name: member.name,
      email: member.email,
      password: '',
      role: member.role,
    });
    setIsEditDialogOpen(true);
  };

  const openLocationDialog = (member) => {
    setSelectedMember(member);
    setLocationLink(member.google_location_link || '');
    setIsLocationDialogOpen(true);
  };

  const saveLocationLink = async () => {
    if (!selectedMember) return;
    
    try {
      await salesApiClient.patch(`/team/${selectedMember.id}/location-link`, {
        google_location_link: locationLink
      });
      
      // Update local state
      setTeamMembers(prev => prev.map(m => 
        m.id === selectedMember.id 
          ? { ...m, google_location_link: locationLink }
          : m
      ));
      
      toast.success('Location link saved successfully');
      setIsLocationDialogOpen(false);
    } catch (error) {
      toast.error('Failed to save location link');
    }
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'admin':
        return 'bg-purple-500/20 text-purple-400';
      case 'manager':
        return 'bg-blue-500/20 text-blue-400';
      case 'sales_rep':
        return 'bg-green-500/20 text-green-400';
      case 'cso':
        return 'bg-amber-500/20 text-amber-400';
      default:
        return 'bg-slate-500/20 text-slate-400';
    }
  };

  const getRoleLabel = (role) => {
    switch (role) {
      case 'admin':
        return 'Director/CEO';
      case 'manager':
        return 'Sales Director';
      case 'sales_rep':
        return 'Sales Executive';
      case 'cso':
        return 'Chief Sales Officer';
      default:
        return role;
    }
  };

  const currentUser = JSON.parse(localStorage.getItem('user') || '{}');

  return (
    <div className="p-6 md:p-8 lg:p-12 space-y-8 fade-in">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl md:text-5xl font-extrabold tracking-tight text-white" data-testid="team-management-title">
            Team Management
          </h1>
          <p className="text-base text-slate-400 mt-2">Add and manage your sales team members</p>
        </div>
        <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
          <DialogTrigger asChild>
            <Button data-testid="add-team-member-button">
              <Plus size={20} className="mr-2" />
              Add Team Member
            </Button>
          </DialogTrigger>
          <DialogOverlay className="bg-black/80" />
          <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="add-member-dialog">
            <DialogHeader>
              <DialogTitle className="text-white">Add New Team Member</DialogTitle>
            </DialogHeader>
            <form onSubmit={handleAddMember} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="name" className="text-slate-200">Full Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="John Doe"
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="member-name-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="email" className="text-slate-200">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="john@aurous.com"
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="member-email-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-200">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="••••••••"
                  required
                  className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                  data-testid="member-password-input"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="role" className="text-slate-200">Role</Label>
                <select
                  id="role"
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 text-white px-3 py-2 text-sm"
                  data-testid="member-role-select"
                >
                  <option value="sales_rep">Sales Executive</option>
                  <option value="manager">Sales Director</option>
                  <option value="cso">Chief Sales Officer</option>
                </select>
              </div>
              <Button type="submit" className="w-full" data-testid="submit-member-button">
                Add Team Member
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Team Members Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="team-members-grid">
        {teamMembers.map((member) => (
          <Card
            key={member.id}
            className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-6"
            data-testid={`team-member-card-${member.id}`}
          >
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="bg-primary/20 p-3 rounded-lg">
                    <Users size={24} className="text-primary" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-white">{member.name}</h3>
                    <span className={`text-xs px-2 py-1 rounded font-medium ${getRoleBadgeColor(member.role)}`}>
                      {getRoleLabel(member.role)}
                    </span>
                  </div>
                </div>
                {member.id !== currentUser.id && (
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => openEditDialog(member)}
                      className="hover:bg-blue-500/10"
                      data-testid={`edit-member-${member.id}`}
                    >
                      <Edit2 size={18} className="text-blue-400" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleDeleteMember(member.id, member.name)}
                      className="hover:bg-red-500/10"
                      data-testid={`delete-member-${member.id}`}
                    >
                      <Trash2 size={18} className="text-red-400" />
                    </Button>
                  </div>
                )}
              </div>

              {/* Details */}
              <div className="space-y-2 pt-2 border-t border-slate-800">
                <div className="flex items-center gap-2 text-sm text-slate-400">
                  <Mail size={16} />
                  <span>{member.email}</span>
                </div>
                {member.role === 'admin' && (
                  <div className="flex items-center gap-2 text-sm text-purple-400">
                    <Shield size={16} />
                    <span>Full Admin Access</span>
                  </div>
                )}
                {member.role === 'manager' && (
                  <div className="flex items-center gap-2 text-sm text-blue-400">
                    <UserCheck size={16} />
                    <span>Team Oversight</span>
                  </div>
                )}
              </div>

              {/* Location Link Button */}
              <div className="pt-2 border-t border-slate-800">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => openLocationDialog(member)}
                  className={`w-full border-slate-700 hover:bg-slate-800 ${
                    member.google_location_link 
                      ? 'text-green-400 border-green-500/30' 
                      : 'text-slate-300'
                  }`}
                >
                  <Link2 size={16} className="mr-2" />
                  {member.google_location_link ? 'Location Linked' : 'Add Location Link'}
                </Button>
              </div>

              {/* Member since */}
              <div className="text-xs text-slate-500 pt-2 border-t border-slate-800">
                Member since: {new Date(member.created_at).toLocaleDateString()}
              </div>
            </div>
          </Card>
        ))}
      </div>

      {/* Edit Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogOverlay className="bg-black/80" />
        <DialogContent className="bg-slate-900 border-slate-700 text-white" data-testid="edit-member-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Edit Team Member</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleEditMember} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="edit-name" className="text-slate-200">Full Name</Label>
              <Input
                id="edit-name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="edit-member-name-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-email" className="text-slate-200">Email</Label>
              <Input
                id="edit-email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                required
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="edit-member-email-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-password" className="text-slate-200">New Password (leave blank to keep current)</Label>
              <Input
                id="edit-password"
                type="password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                placeholder="••••••••"
                className="bg-slate-800 border-slate-700 text-white placeholder:text-slate-500"
                data-testid="edit-member-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-role" className="text-slate-200">Role</Label>
              <select
                id="edit-role"
                value={formData.role}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="flex h-10 w-full rounded-md border border-slate-700 bg-slate-800 text-white px-3 py-2 text-sm"
                data-testid="edit-member-role-select"
              >
                <option value="sales_rep">Sales Executive</option>
                <option value="manager">Sales Director</option>
                <option value="cso">Chief Sales Officer</option>
              </select>
            </div>
            <Button type="submit" className="w-full" data-testid="submit-edit-member-button">
              Update Team Member
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Location Link Dialog */}
      <Dialog open={isLocationDialogOpen} onOpenChange={setIsLocationDialogOpen}>
        <DialogOverlay className="bg-black/80" />
        <DialogContent className="bg-slate-900 border-slate-700 text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              <MapPin className="w-5 h-5 text-blue-400" />
              Location Settings - {selectedMember?.name}
            </DialogTitle>
          </DialogHeader>
          
          {selectedMember && (
            <div className="space-y-4">
              <div className="bg-slate-800/50 rounded-xl p-4">
                <h4 className="text-sm font-medium text-white mb-2">Google Maps Location Link</h4>
                <p className="text-xs text-slate-400 mb-3">
                  Paste a Google Maps link below. For real-time GPS extraction, use a link with coordinates (see instructions).
                </p>
                <Input
                  value={locationLink}
                  onChange={(e) => setLocationLink(e.target.value)}
                  placeholder="https://www.google.com/maps/@3.1234,101.5678,15z"
                  className="bg-slate-800 border-slate-700 text-white"
                />
              </div>

              <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                <h4 className="text-sm font-medium text-blue-300 mb-2">How to get a location link with GPS:</h4>
                <ol className="text-xs text-slate-400 space-y-2">
                  <li><span className="text-green-400">Option A (Best for GPS extraction):</span></li>
                  <li>1. Open Google Maps on phone/computer</li>
                  <li>2. Long-press on the current location (blue dot)</li>
                  <li>3. Tap "Share" and copy the link</li>
                  <li>4. Link will look like: <span className="text-blue-300">google.com/maps/@3.123,101.456,15z</span></li>
                </ol>
                <div className="mt-3 pt-3 border-t border-slate-700">
                  <ol className="text-xs text-slate-400 space-y-2">
                    <li><span className="text-yellow-400">Option B (Live sharing - viewable only):</span></li>
                    <li>1. Google Maps → Profile → "Location sharing"</li>
                    <li>2. Share "Until you turn this off"</li>
                    <li>3. This creates a live link (opens in iframe)</li>
                  </ol>
                </div>
              </div>

              {selectedMember.google_location_link && (
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    className="flex-1 border-slate-700 text-slate-300"
                    onClick={() => window.open(selectedMember.google_location_link, '_blank')}
                  >
                    <ExternalLink size={16} className="mr-2" />
                    View Current Location
                  </Button>
                </div>
              )}

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1 border-slate-700 text-slate-300"
                  onClick={() => setIsLocationDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="flex-1 bg-blue-600 hover:bg-blue-700"
                  onClick={saveLocationLink}
                >
                  Save Location Link
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {teamMembers.length === 0 && (
        <Card className="bg-slate-900 border border-slate-800 rounded-xl shadow-sm p-12 text-center" data-testid="no-team-members">
          <Users size={48} className="mx-auto text-slate-700 mb-4" />
          <p className="text-slate-400">No team members yet. Add your first team member to get started.</p>
        </Card>
      )}
    </div>
  );
}
